import { useState } from "react";
import { ArrowUpRight, Music2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const PLATFORMS = [
  { key: "spotify", label: "Spotify", slug: "spotify" },
  { key: "apple", label: "Apple Music", slug: "applemusic" },
  { key: "amazon", label: "Amazon Music", slug: "" },
  { key: "pandora", label: "Pandora", slug: "pandora" },
  { key: "youtube", label: "YouTube / YouTube Music", slug: "youtube" },
];

function PlatformIcon({ slug }) {
  if (!slug) return <Music2 size={18} aria-hidden="true" className="shrink-0" />;
  return (
    <img
      src={`https://cdn.simpleicons.org/${slug}/090909`}
      alt=""
      aria-hidden="true"
      loading="lazy"
      className="w-[18px] h-[18px] shrink-0"
    />
  );
}

export default function ReleaseCard({ release, index }) {
  const [open, setOpen] = useState(false);
  const links = PLATFORMS.filter((p) => release.links?.[p.key]);

  return (
    <>
      <article
        data-testid={`release-card-${index}`}
        className="group border border-ink/15 bg-paper overflow-hidden flex flex-col"
      >
        <div className="aspect-square overflow-hidden bg-ink">
          <img
            src={release.artwork}
            alt={`${release.title} artwork`}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
          />
        </div>
        <div className="p-5 md:p-6 flex flex-col grow">
          <p className="text-[10px] font-semibold tracking-[0.25em] uppercase text-fog">
            {release.type}
          </p>
          <h3 className="mt-1.5 font-spartan font-bold text-lg leading-tight">{release.title}</h3>
          <button
            type="button"
            data-testid={`release-listen-button-${index}`}
            onClick={() => setOpen(true)}
            className="mt-5 inline-flex w-fit items-center gap-2 bg-ink text-paper text-[11px] font-semibold tracking-[0.22em] uppercase px-6 py-3 rounded-full hover:bg-charcoal transition-colors duration-300"
          >
            Listen Now
          </button>
        </div>
      </article>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid={`release-modal-${index}`}
          className="bg-paper border-ink/15 text-ink max-w-md p-0 overflow-hidden sm:rounded-none"
        >
          <div aria-hidden="true" className="absolute inset-0">
            <img src={release.artwork} alt="" className="w-full h-full object-cover scale-150 blur-2xl opacity-30" />
            <div className="absolute inset-0 bg-paper/55" />
          </div>
          <div className="relative">
            <div className="aspect-square w-full overflow-hidden">
              <img src={release.artwork} alt={`${release.title} artwork`} className="w-full h-full object-cover" />
            </div>
            <div className="p-6 md:p-8">
              <p className="text-[10px] font-semibold tracking-[0.25em] uppercase text-fog">{release.type}</p>
              <DialogTitle className="mt-1.5 font-spartan font-bold text-2xl tracking-tight">
                {release.title}
              </DialogTitle>
              {links.length > 0 ? (
                <ul className="mt-6 divide-y divide-ink/10 border-t border-b border-ink/10">
                  {links.map((platform) => (
                    <li key={platform.key}>
                      <a
                        href={release.links[platform.key]}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`release-link-${platform.key}-${index}`}
                        className="flex items-center justify-between py-3.5 text-sm font-medium hover:pl-2 transition-[padding] duration-300"
                      >
                        <span className="flex items-center gap-3.5">
                          <PlatformIcon slug={platform.slug} />
                          {platform.label}
                        </span>
                        <ArrowUpRight size={16} aria-hidden="true" />
                      </a>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-6 text-sm text-fog">Streaming links coming soon.</p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
